import React from "react";

interface WidgetErrorFallbackProps {
  error?: Error;
  resetErrorBoundary?: () => void;
  primaryColor?: string;
  position?: "left" | "right";
}

/**
 * A specialized error fallback component for the AIChatWidget
 * Designed to match the widget's styling and UX
 */
export const WidgetErrorFallback: React.FC<WidgetErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  primaryColor = "#007bff",
  position = "right",
}) => {
  // Convert primary color to a lighter background color
  const getBgColor = (color: string): string => {
    // Simple lightening - in production you might want a more sophisticated approach
    return `${color}15`; // 15 is hex for low opacity
  };

  return (
    <div
      className={`ai-chat-widget-error ${position}`}
      style={{
        position: "fixed",
        bottom: "20px",
        [position]: "20px",
        maxWidth: "350px",
        width: "100%",
        padding: "15px",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        backgroundColor: "#fff",
        border: `1px solid ${primaryColor}`,
        zIndex: 10000,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
          padding: "0 0 10px 0",
          borderBottom: "1px solid #eee",
        }}
      >
        <h3
          style={{
            margin: 0,
            color: primaryColor,
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          Chat Widget Error
        </h3>
        {resetErrorBoundary && (
          <button
            onClick={resetErrorBoundary}
            style={{
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "20px",
              lineHeight: 1,
              color: "#666",
              padding: "0 5px",
            }}
            aria-label="Close error message"
          >
            ×
          </button>
        )}
      </div>

      <p
        style={{
          margin: "0 0 15px 0",
          fontSize: "14px",
          color: "#333",
        }}
      >
        The chat widget encountered an error. Please try again.
      </p>

      {error && (
        <details
          style={{
            fontSize: "12px",
            color: "#666",
            marginBottom: "15px",
          }}
        >
          <summary style={{ cursor: "pointer" }}>Error Details</summary>
          <p
            style={{
              margin: "10px 0",
              padding: "8px",
              backgroundColor: getBgColor(primaryColor),
              borderRadius: "4px",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {error.message}
          </p>
        </details>
      )}

      {resetErrorBoundary && (
        <button
          onClick={resetErrorBoundary}
          style={{
            display: "block",
            width: "100%",
            padding: "8px 16px",
            backgroundColor: primaryColor,
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          Reload Widget
        </button>
      )}
    </div>
  );
};
